import { useSubscription } from "@apollo/client";
import { get } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import graphql from "utils/api/graphql";
import { HOT_LIST } from "utils/constants";

const pruneExpired = (movements, now) =>
  Object.keys(movements).reduce((accumulator, id) => {
    if (movements[id].expiresAt > now) accumulator[id] = movements[id];
    return accumulator;
  }, {});

const mergeRises = (current, previous, rows, now) => {
  const active = pruneExpired(current, now);
  return rows.reduce(
    (accumulator, row) => {
      const before = previous[row.id];
      if (!before) return accumulator;
      const existing = active[row.id];
      const startScore = existing ? existing.startScore : before.heatScore || 0;
      const startTier = existing ? existing.startTier : before.heatTier || 0;
      const rise = Math.round((row.heatScore || 0) - startScore);
      const tierUp = (row.heatTier || 0) > startTier;
      if (rise < 1 && !tierUp) return accumulator;
      if (existing && existing.rise === rise && existing.tierUp === tierUp) {
        return accumulator;
      }
      accumulator[row.id] = {
        startScore,
        startTier,
        rise,
        tierUp,
        enteredHot:
          get(existing, "enteredHot", false) ||
          (row.heatTier === HOT_LIST.HOT_TIER &&
            startTier !== HOT_LIST.HOT_TIER),
        expiresAt: now + HOT_LIST.MOVE_HIGHLIGHT_MS,
      };
      return accumulator;
    },
    { ...active },
  );
};

const appendHistory = (history, rows, now) =>
  rows.reduce((accumulator, row) => {
    accumulator[row.id] = [
      ...(history[row.id] || []).filter(
        (sample) => now - sample.at <= HOT_LIST.DECAY_WINDOW_MS,
      ),
      { at: now, score: row.heatScore || 0 },
    ];
    return accumulator;
  }, {});

const resolveDecays = (history, now) =>
  Object.keys(history).reduce((accumulator, id) => {
    const samples = history[id];
    const baseline = samples[0];
    const latest = samples[samples.length - 1];
    if (now - baseline.at < HOT_LIST.DECAY_MIN_AGE_MS) return accumulator;
    const decline = baseline.score - latest.score;
    if (decline >= HOT_LIST.DECAY_MIN_POINTS) {
      accumulator[id] = Math.round(decline * 10) / 10;
    }
    return accumulator;
  }, {});

const indexRows = (rows) =>
  rows.reduce((accumulator, row) => {
    accumulator[row.id] = {
      heatScore: row.heatScore,
      heatTier: row.heatTier,
    };
    return accumulator;
  }, {});

const keepOrder = (order, rows, snapshot) => {
  const fresh = rows.reduce((accumulator, row) => {
    accumulator[row.id] = row;
    return accumulator;
  }, {});
  return order.map((id) => fresh[id] || snapshot[id]).filter(Boolean);
};

const useHotListRows = ({ data = [], hotMode, organizationId, refetch }) => {
  const [movements, setMovements] = useState({});
  const [decays, setDecays] = useState({});
  const scoreHistory = useRef({});
  const [frozen, setFrozen] = useState(null);
  const previousRows = useRef({});
  const latestRows = useRef([]);
  const pendingRefetch = useRef(false);
  const debounceTimer = useRef(null);

  const runRefetch = useCallback(() => {
    if (document.visibilityState === "hidden") {
      pendingRefetch.current = true;
      return;
    }
    pendingRefetch.current = false;
    if (typeof refetch === "function") refetch();
  }, [refetch]);

  const scheduleRefetch = useCallback(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(
      runRefetch,
      HOT_LIST.REFETCH_DEBOUNCE_MS,
    );
  }, [runRefetch]);

  useSubscription(graphql.subscriptions.PROJECT_HEAT_CHANGED, {
    skip: !hotMode || !organizationId,
    variables: { where: { organizationId } },
    onData: scheduleRefetch,
  });

  useEffect(() => {
    if (!hotMode) {
      previousRows.current = {};
      scoreHistory.current = {};
      setMovements({});
      setDecays({});
      return;
    }
    const rows = data || [];
    latestRows.current = rows;
    const previous = previousRows.current;
    const now = Date.now();
    previousRows.current = indexRows(rows);
    scoreHistory.current = appendHistory(scoreHistory.current, rows, now);
    setDecays(resolveDecays(scoreHistory.current, now));
    setMovements((current) => mergeRises(current, previous, rows, now));
  }, [data, hotMode]);

  useEffect(() => {
    const expiries = Object.values(movements).map(
      (movement) => movement.expiresAt,
    );
    if (!expiries.length) return undefined;
    const timer = setTimeout(
      () => setMovements((current) => pruneExpired(current, Date.now())),
      Math.max(0, Math.min(...expiries) - Date.now()) + 50,
    );
    return () => clearTimeout(timer);
  }, [movements]);

  useEffect(() => () => clearTimeout(debounceTimer.current), []);

  useEffect(() => {
    if (!hotMode) return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible" && pendingRefetch.current) {
        runRefetch();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [hotMode, runRefetch]);

  const onMouseEnter = useCallback(() => {
    const rows = latestRows.current;
    setFrozen({
      order: rows.map((row) => row.id),
      snapshot: rows.reduce((accumulator, row) => {
        accumulator[row.id] = row;
        return accumulator;
      }, {}),
    });
  }, []);

  const onMouseLeave = useCallback(() => setFrozen(null), []);

  const rows = useMemo(
    () =>
      hotMode && frozen
        ? keepOrder(frozen.order, data || [], frozen.snapshot)
        : data,
    [data, frozen, hotMode],
  );

  const rowClassName = useCallback(
    (record) => {
      if (!hotMode) return "";
      const tierName = get(HOT_LIST.TIERS, get(record, "heatTier"));
      const movement = movements[record.id];
      return [
        tierName && `hot-row hot-row--${tierName}`,
        movement && "hot-row--moved-up",
        movement && movement.enteredHot && "hot-row--entered-hot",
      ]
        .filter(Boolean)
        .join(" ");
    },
    [hotMode, movements],
  );

  return {
    rows,
    movements,
    decays,
    rowClassName,
    isFrozen: hotMode && !!frozen,
    hoverHandlers: hotMode ? { onMouseEnter, onMouseLeave } : {},
  };
};

export default useHotListRows;
